import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

interface UseKeyboardSelectableListOptions<T> {
  isOpen: boolean;
  items: T[];
  onSelect: (item: T) => void;
  onClose?: () => void;
  autoHighlightFirst?: boolean;
}

export default function useKeyboardSelectableList<T>({
  isOpen,
  items,
  onSelect,
  onClose,
  autoHighlightFirst = true,
}: UseKeyboardSelectableListOptions<T>) {
  const [activeIndex, setActiveIndex] = useState(-1);
  const itemRefs = useRef<Array<HTMLElement | null>>([]);

  useEffect(() => {
    itemRefs.current = itemRefs.current.slice(0, items.length);
  }, [items.length]);

  useEffect(() => {
    if (!isOpen) {
      queueMicrotask(() => {
        setActiveIndex(-1);
      });
      return;
    }

    if (items.length === 0) {
      queueMicrotask(() => {
        setActiveIndex(-1);
      });
      return;
    }

    queueMicrotask(() => {
      setActiveIndex((previousIndex) => {
        if (previousIndex >= 0 && previousIndex < items.length) {
          return previousIndex;
        }

        return autoHighlightFirst ? 0 : -1;
      });
    });
  }, [autoHighlightFirst, isOpen, items.length]);

  useEffect(() => {
    if (activeIndex < 0) {
      return;
    }

    itemRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const handleKeyDown = useCallback((event: ReactKeyboardEvent<HTMLElement>) => {
    if (!isOpen) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (items.length === 0) {
        return;
      }
      setActiveIndex((previousIndex) => {
        if (previousIndex < 0) {
          return 0;
        }
        return (previousIndex + 1) % items.length;
      });
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (items.length === 0) {
        return;
      }
      setActiveIndex((previousIndex) => {
        if (previousIndex < 0) {
          return items.length - 1;
        }
        return (previousIndex - 1 + items.length) % items.length;
      });
      return;
    }

    if (event.key === 'Home') {
      if (items.length === 0) {
        return;
      }
      event.preventDefault();
      setActiveIndex(0);
      return;
    }

    if (event.key === 'End') {
      if (items.length === 0) {
        return;
      }
      event.preventDefault();
      setActiveIndex(items.length - 1);
      return;
    }

    if (event.key === 'Enter') {
      if (items.length === 0) {
        return;
      }
      event.preventDefault();
      const selectedIndex = activeIndex >= 0 ? activeIndex : 0;
      const selectedItem = items[selectedIndex];
      if (selectedItem) {
        onSelect(selectedItem);
      }
      return;
    }

    if (event.key === 'Escape') {
      onClose?.();
    }
  }, [activeIndex, isOpen, items, onClose, onSelect]);

  const registerItemRef = useCallback((index: number) => (element: HTMLElement | null) => {
    itemRefs.current[index] = element;
  }, []);

  return {
    activeIndex,
    setActiveIndex,
    handleKeyDown,
    registerItemRef,
  };
}
